// File: frontend/src/pages/Plato/components/ParameterSelector/ParameterSelector.js

import React, { useState } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { X, Save, RotateCcw } from 'lucide-react';
import styles from './ParameterSelector.module.css';

const ParameterSelector = ({ parameters, selectedParameters, onParameterChange, onClose }) => {
  const { t } = useLanguage();
  const [localParameters, setLocalParameters] = useState({ ...selectedParameters });
  
  const handleParameterChange = (paramName, value) => {
    setLocalParameters(prev => ({
      ...prev,
      [paramName]: value
    }));
  };
  
  const handleSave = () => {
    // Update all parameters at once
    Object.entries(localParameters).forEach(([paramName, value]) => {
      onParameterChange(paramName, value);
    });
    onClose();
  };
  
  const handleReset = () => {
    // Reset to defaults
    const defaultParams = {};
    Object.entries(parameters).forEach(([key, config]) => {
      defaultParams[key] = config.type === 'object' ? {} : config.default;
    });
    setLocalParameters(defaultParams);
  };
  
  const renderParameterControl = (paramName, paramConfig) => {
    const value = localParameters[paramName];
    
    switch (paramConfig.type) {
      case 'integer':
      case 'number':
        return (
          <div className={styles.sliderContainer}>
            <input
              type="range"
              min={paramConfig.min}
              max={paramConfig.max}
              step={paramConfig.step}
              value={value}
              onChange={(e) => handleParameterChange(paramName, Number(e.target.value))}
              className={styles.slider}
            />
            <span className={styles.sliderValue}>{value}</span>
          </div>
        );
        
      case 'string':
        return (
          <select
            value={value}
            onChange={(e) => handleParameterChange(paramName, e.target.value)}
            className={styles.select}
          >
            {paramConfig.options.map(option => (
              <option key={option} value={option}>
                {t(`plato.parameters.${paramName}.options.${option}`, option)}
              </option>
            ))}
          </select>
        );
        
      case 'boolean':
        return (
          <label className={styles.toggleContainer}>
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => handleParameterChange(paramName, e.target.checked)}
              className={styles.toggleInput}
            />
            <span className={styles.toggle}></span>
          </label>
        );
        
      case 'object':
        if (Array.isArray(paramConfig.options)) {
          return (
            <div className={styles.toneSelectors}>
              {paramConfig.options.map((option, index) => (
                <button
                  key={option}
                  className={`${styles.toneButton} ${
                    value[0] === option ? styles.selected : ''
                  }`}
                  onClick={() => handleParameterChange(paramName, [option, paramConfig.values[index]])}
                >
                  {t(`plato.parameters.${paramName}.options.${option}`, option)}
                </button>
              ))}
              {value[0] && (
                <div className={styles.toneSliderContainer}>
                  <span>{t('plato.parameters.intensity')}</span>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.1}
                    value={value[1] || 0.5}
                    onChange={(e) => handleParameterChange(paramName, [value[0], Number(e.target.value)])}
                    className={styles.toneSlider}
                  />
                  <span className={styles.toneValue}>{(value[1] || 0.5) * 100}%</span>
                </div>
              )}
            </div>
          );
        }
        return null;
        
      default:
        return null;
    }
  };
  
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>{t('plato.parameter_selector.title')}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className={styles.content}>
          {Object.entries(parameters).map(([paramName, paramConfig]) => (
            <div key={paramName} className={styles.parameterItem}>
              <div className={styles.parameterLabel}>
                {t(`plato.parameters.${paramName}.label`, paramName)}
                <span className={styles.parameterDescription}>
                  {t(`plato.parameters.${paramName}.description`, '')}
                </span>
              </div>
              <div className={styles.parameterControl}>
                {renderParameterControl(paramName, paramConfig)}
              </div>
            </div>
          ))}
          
          <div className={styles.actions}>
            <button className={styles.resetButton} onClick={handleReset}>
              <RotateCcw size={16} />
              {t('plato.parameter_selector.reset')}
            </button>
            <button className={styles.saveButton} onClick={handleSave}>
              <Save size={16} />
              {t('plato.parameter_selector.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParameterSelector;